import React, { useState, ChangeEvent, useMemo } from "react";
import Button from "src/components/core/Button";
import Dialog from "src/components/common/Dialog";
import { Input } from "src/components/core/Input";
import { useCombineListOfLeadsMutation } from "../hooks/useCombineListOfLeadsMutation";
import { useUpdateSavedSearchMutation } from "../hooks/useUpdateSavedSearchMutation";
import { SavedSearchesSelector } from "../search/SavedSearchesSelector";
import { useMultiSelectCombobox } from "src/components/core/MultiSelectComboBox";
import { useNavigate } from "react-router-dom";
import { toast } from "src/components/shadcn/useToast";
import { SavedSearch } from "src/types/neighborhoodSearch";
import { useFetchCreditsBalance } from "../hooks/useFetchCreditsBalance";
import { SearchErrorMessage } from "../search/SearchSidebar/SearchErrorMessage";
import { isNullOrUndefined } from "src/utils/objects";

export interface CombineListsDialogProps {
  onClose: () => void;
  open: boolean;
  initialSelectedSearches?: SavedSearch[];
}

export const CombineListsDialog: React.FC<CombineListsDialogProps> = ({
  onClose,
  open,
  initialSelectedSearches = [],
}) => {
  const [newSearchName, setNewSearchName] = useState("");
  const [selectedSearches, setSelectedSearches] = useState<SavedSearch[]>(
    initialSelectedSearches
  );
  const navigate = useNavigate();

  const { mutateAsync: combineListOfLeads, isPending: isCombiningLists } =
    useCombineListOfLeadsMutation();
  const { mutateAsync: updateSavedSearch, isPending: isUpdatingSavedSearch } =
    useUpdateSavedSearchMutation();
  const { data: creditsData } = useFetchCreditsBalance();
  const availableCredits = creditsData?.balance;

  const handleSelectedSearchesChange = (newSelectedSearches: SavedSearch[]) => {
    setSelectedSearches(newSelectedSearches);
  };

  const totalProperties = useMemo(() => {
    return selectedSearches.reduce(
      (acc, search) => acc + search.result_count,
      0
    );
  }, [selectedSearches]);

  const hasInsufficientCredits = useMemo(() => {
    return !isNullOrUndefined(availableCredits) && availableCredits < totalProperties;
  }, [availableCredits, totalProperties]);

  const {
    selectedItems: selectedSearchesOptions,
    handleSelect: handleSearchSelect,
    handleRemove: handleSearchRemove,
  } = useMultiSelectCombobox({
    initialSelectedItems: initialSelectedSearches.map((item) => ({
      label: item.name,
      value: item.id,
    })),
  });

  const isSaving = isCombiningLists || isUpdatingSavedSearch;

  const selectionsCommaSeparated = useMemo(
    () => selectedSearchesOptions?.map((item) => item.label).join(", "),
    [selectedSearchesOptions]
  );

  const handleSubmitClick = async () => {
    const { id: newListId } = await combineListOfLeads({
      listIds: selectedSearchesOptions.map((item) => item.value as string),
    });
    if (newListId) {
      await updateSavedSearch({
        listId: newListId,
        leadList: {
          name: newSearchName,
        },
      });
      navigate(`/ns/searches/map?selected=${newListId}`);
      toast({
        title: `${newSearchName} created`,
        description: (
          <div
            dangerouslySetInnerHTML={{
              __html: `Properties from <strong>${selectionsCommaSeparated}</strong> have been combined into <strong>${newSearchName}</strong>`,
            }}
          />
        ),
        variant: "success",
      });
      onClose();
    }

    navigate(`/ns/searches/map?selected=${newListId}`);
    onClose();
  };

  return (
    <Dialog onOpenChange={onClose} isOpen={open} title="Combine Lists">
      <div className="flex flex-col gap-5 mt-5">
        {selectedSearchesOptions?.length < 2 && (
          <>
            <div>
              Select two or more lists to combine into a new list. The new list
              will contain properties from all selected lists.
            </div>
            <SavedSearchesSelector
              selectedItems={selectedSearchesOptions}
              onSelect={handleSearchSelect}
              onRemove={handleSearchRemove}
              onSelectedSearchesChange={handleSelectedSearchesChange}
            />
          </>
        )}
        {selectedSearchesOptions?.length > 1 && (
          <>
            {hasInsufficientCredits ? (
              <div className="text-sm text-gray-500 text-center p-4">
                <SearchErrorMessage
                  error={{ name: "MonthlyLimitExceeded", message: "" }}
                />
              </div>
            ) : (
              <>
                <div>
                  Combine properties from{" "}
                  <strong>
                    {selectedSearchesOptions
                      .map((item) => item.label)
                      .join(", ")}
                  </strong>{" "}
                  into a new list.
                </div>
                <Input
                  autoFocus
                  value={newSearchName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setNewSearchName(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newSearchName && !isSaving && !hasInsufficientCredits) {
                      handleSubmitClick();
                    }
                  }}
                  placeholder={"Enter a name for the combined list"}
                />
                <div>
                  Click <strong>Combine Lists</strong> to combine the properties from{" "}
                  <strong>
                    {selectedSearchesOptions
                      .map((item) => item.label)
                      .join(", ")}
                  </strong>
                  . Contact information will not be updated.
                </div>
                <div className="flex flex-row gap-2 justify-end mt-8">
                  <Button color="secondary" onClick={onClose} variant="outline">
                    Cancel
                  </Button>
                  <Button
                    disabled={!newSearchName}
                    color="primary"
                    variant="default"
                    onClick={handleSubmitClick}
                    isLoading={isSaving}
                  >
                    Combine Lists
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
